import { useEffect, useRef, useState } from 'react';

/*
 * Hand-drawn SVG charts for the AH dashboard — ported from the design handoff's
 * charts.jsx. Thin strokes (1.4–1.6px), soft gradient area fills, no chartjunk.
 * Kept as bespoke SVG (not Recharts) to match the editorial style exactly.
 */

// ---------- Sparkline ----------
export function Sparkline({ data, color = 'var(--accent)', height = 28, fill = true }: { data: number[]; color?: string; height?: number; fill?: boolean }) {
  const w = 200;
  const h = height;
  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;
  const step = w / (data.length - 1);
  const pts = data.map((v, i) => [i * step, h - ((v - min) / range) * (h - 4) - 2] as const);
  const linePath = pts.map((p, i) => (i === 0 ? `M${p[0]},${p[1]}` : `L${p[0]},${p[1]}`)).join(' ');
  const areaPath = `${linePath} L${w},${h} L0,${h} Z`;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" style={{ width: '100%', height: h, display: 'block' }}>
      {fill && <path d={areaPath} fill={color} opacity="0.10" />}
      <path d={linePath} fill="none" stroke={color} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// ---------- Area / Line chart with hover ----------
type HoverState = { idx: number; x: number; y: number; clientX: number; clientY: number };

function fmtAxis(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1).replace(/\.0$/, '')}k`;
  return String(Math.round(n));
}

// Round up to a "nice" axis maximum (1 / 2 / 5 × 10ⁿ) so the y-axis tracks the
// data's own magnitude instead of a fixed absolute ceiling — a ~900/day series
// fills the chart instead of hugging the bottom of a 50k axis. Never 0 (NaN guard).
function niceCeil(n: number): number {
  if (!(n > 0)) return 1;
  const mag = Math.pow(10, Math.floor(Math.log10(n)));
  const norm = n / mag; // 1..<10
  const step = norm <= 1 ? 1 : norm <= 2 ? 2 : norm <= 5 ? 5 : 10;
  return step * mag;
}

export function AreaChart({
  data,
  days,
  color = 'var(--accent)',
  label = 'Value',
  height = 220,
}: {
  data: number[];
  days: Date[];
  color?: string;
  label?: string;
  height?: number;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [hover, setHover] = useState<HoverState | null>(null);
  const [size, setSize] = useState({ w: 800, h: height });

  useEffect(() => {
    const obs = new ResizeObserver((entries) => {
      for (const e of entries) setSize({ w: e.contentRect.width, h: height });
    });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [height]);

  const padL = 44;
  const padR = 12;
  const padT = 16;
  const padB = 26;
  const innerW = size.w - padL - padR;
  const innerH = size.h - padT - padB;
  const max = Math.max(0, ...data);
  const niceMax = niceCeil(max); // scale axis to the data's max, not a fixed 50k
  const step = data.length > 1 ? innerW / (data.length - 1) : innerW;
  const pts = data.map((v, i) => [padL + i * step, padT + innerH - (v / niceMax) * innerH] as const);
  const linePath = pts.map((p, i) => (i === 0 ? `M${p[0]},${p[1]}` : `L${p[0]},${p[1]}`)).join(' ');
  // Guard the empty case (no data points) so we don't index pts[-1].
  const areaPath = pts.length ? `${linePath} L${pts[pts.length - 1][0]},${padT + innerH} L${pts[0][0]},${padT + innerH} Z` : '';

  const ticks = 4;
  const yTicks = Array.from({ length: ticks + 1 }, (_, i) => (niceMax / ticks) * i);

  const labelEvery = Math.ceil(data.length / 6);
  const gradId = `ah-grad-${label.replace(/\s/g, '')}`;

  function onMove(e: React.MouseEvent<HTMLDivElement>) {
    if (!ref.current || !pts.length) return;
    const rect = ref.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const ratio = (x - padL) / innerW;
    const idx = Math.max(0, Math.min(data.length - 1, Math.round(ratio * (data.length - 1))));
    setHover({ idx, x: pts[idx][0], y: pts[idx][1], clientX: e.clientX, clientY: e.clientY });
  }

  return (
    <div ref={ref} className="trend-chart" style={{ height }} onMouseMove={onMove} onMouseLeave={() => setHover(null)}>
      <svg viewBox={`0 0 ${size.w} ${size.h}`} width={size.w} height={size.h} style={{ display: 'block' }}>
        {yTicks.map((t, i) => {
          const y = padT + innerH - (t / niceMax) * innerH;
          return (
            <g key={i}>
              <line x1={padL} x2={size.w - padR} y1={y} y2={y} stroke="var(--line)" strokeWidth="1" />
              <text x={padL - 8} y={y + 4} textAnchor="end" fontSize="10" fill="var(--ink-4)" style={{ fontFeatureSettings: '"tnum"' }}>
                {fmtAxis(t)}
              </text>
            </g>
          );
        })}
        <defs>
          <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.22" />
            <stop offset="100%" stopColor={color} stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={areaPath} fill={`url(#${gradId})`} />
        <path d={linePath} fill="none" stroke={color} strokeWidth="1.6" strokeLinejoin="round" strokeLinecap="round" />
        {/* A line needs ≥2 points to be visible; for a 1–2 day window (e.g. early
            in the campaign, when we only plot up to today) draw point markers so
            the data doesn't render as a blank chart. */}
        {pts.length <= 2 && pts.map((p, i) => <circle key={`pt-${i}`} cx={p[0]} cy={p[1]} r="3.5" fill={color} />)}
        {days.map((d, i) =>
          i % labelEvery === 0 || i === days.length - 1 ? (
            <text key={i} x={padL + i * step} y={size.h - 8} fontSize="10" fill="var(--ink-4)" textAnchor="middle">
              {d.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' })}
            </text>
          ) : null,
        )}
        {hover && (
          <g>
            <line x1={hover.x} x2={hover.x} y1={padT} y2={padT + innerH} stroke="var(--ink)" strokeWidth="1" strokeDasharray="3 3" opacity="0.4" />
            <circle cx={hover.x} cy={hover.y} r="4" fill="var(--bg)" stroke={color} strokeWidth="2" />
          </g>
        )}
      </svg>
      {hover && (
        <div className="tooltip visible" style={{ left: hover.clientX + 12, top: hover.clientY - 60 }}>
          <div className="t-label">{days[hover.idx].toLocaleDateString('nl-NL', { weekday: 'short', day: 'numeric', month: 'long' })}</div>
          <div className="t-value">{data[hover.idx].toLocaleString('nl-NL')}</div>
        </div>
      )}
    </div>
  );
}

// ---------- Donut ----------
export function Donut({ segments, size = 140, thickness = 18 }: { segments: Array<{ value: number; color: string }>; size?: number; thickness?: number }) {
  const total = segments.reduce((a, s) => a + s.value, 0);
  const r = size / 2 - thickness / 2;
  const c = 2 * Math.PI * r;
  let acc = 0;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="donut">
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--bg-sunken)" strokeWidth={thickness} />
      {segments.map((s, i) => {
        const len = (s.value / total) * c;
        const dash = `${len} ${c - len}`;
        const offset = -acc;
        acc += len;
        return (
          <circle
            key={i}
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill="none"
            stroke={s.color}
            strokeWidth={thickness}
            strokeDasharray={dash}
            strokeDashoffset={offset}
            transform={`rotate(-90 ${size / 2} ${size / 2})`}
            strokeLinecap="butt"
          />
        );
      })}
      <text
        x={size / 2}
        y={size / 2 - 4}
        textAnchor="middle"
        fontFamily="var(--serif)"
        fontSize="22"
        fill="var(--ink)"
        style={{ fontFeatureSettings: '"tnum"' }}
      >
        {total.toFixed(0)}%
      </text>
      <text x={size / 2} y={size / 2 + 14} textAnchor="middle" fontSize="9" fill="var(--ink-4)" style={{ textTransform: 'uppercase', letterSpacing: '0.08em' }}>
        Totaal
      </text>
    </svg>
  );
}
